import React, {
  FC,
  PropsWithChildren,
  useState,
  useRef,
  useMemo,
  useCallback,
  Children,
  isValidElement,
  useEffect,
} from 'react';
import IconButton from 'components/theme/IconButton/IconButton';
import usePrevious from 'hooks/usePrevious';
import IconLeft from 'assets/chevron-left';
import IconRight from 'assets/chevron-right';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Wrapper, Slider } from './styled';

const ArrowLeft = styled(IconButton)<{ isFullScreenImageModalOpen?: boolean }>`
  position: absolute;
  top: ${({ isFullScreenImageModalOpen }) =>
    isFullScreenImageModalOpen ? '50%' : '40%'};
  z-index: 20;
  left: ${({ isFullScreenImageModalOpen }) =>
    isFullScreenImageModalOpen ? '-83px' : '20px'};
  background-color: ${({ isFullScreenImageModalOpen }) =>
    isFullScreenImageModalOpen
      ? 'white'
      : 'var(--way-colors-contrastColorShades-50)'};

  &:hover {
    background-color: ${({ isFullScreenImageModalOpen }) =>
      isFullScreenImageModalOpen
        ? 'white'
        : 'var(--way-colors-contrastColorShades-70)'};
  }
  @media (max-width: 768px) {
    ${({ isFullScreenImageModalOpen }) =>
      isFullScreenImageModalOpen && 'display: none;'}
  }
`;

const ArrowRight = styled(IconButton)<{ isFullScreenImageModalOpen?: boolean }>`
  position: absolute;
  top: ${({ isFullScreenImageModalOpen }) =>
    isFullScreenImageModalOpen ? '50%' : '40%'};
  z-index: 20;
  right: ${({ isFullScreenImageModalOpen }) =>
    isFullScreenImageModalOpen ? '-83px' : '20px'};
  background-color: ${({ isFullScreenImageModalOpen }) =>
    isFullScreenImageModalOpen
      ? 'white'
      : 'var(--way-colors-contrastColorShades-50)'};

  &:hover {
    background-color: ${({ isFullScreenImageModalOpen }) =>
      isFullScreenImageModalOpen
        ? 'white'
        : 'var(--way-colors-contrastColorShades-70)'};
  }
  @media (max-width: 768px) {
    ${({ isFullScreenImageModalOpen }) =>
      isFullScreenImageModalOpen && 'display: none;'}
  }
`;

type Props = {
  showArrows?: boolean;
  fit?: 'contain' | 'cover';
  onSlideChange?: (index: number) => void;
  isFullScreenImageModalOpen?: boolean;
  selectedSlideIndex: number;
};

const MinimalCarousel: FC<PropsWithChildren<Props>> = ({
  showArrows = true,
  selectedSlideIndex = 0,
  fit = 'cover',
  onSlideChange,
  children,
  isFullScreenImageModalOpen,
}) => {
  const { t } = useTranslation();
  const sliderRef = useRef<HTMLDivElement>(null);
  const hasScrolledOnceRef = useRef(false);
  const firstLoadRef = useRef(true);
  const [currentIndex, setCurrentIndex] = useState(selectedSlideIndex);
  const previousSelectedTabIndex = usePrevious(selectedSlideIndex);
  const [childrenLeftCoords, setChildrenLeftCoords] = useState<number[]>([]);

  const refreshChildrenLeftCoords = useCallback(() => {
    if (sliderRef.current) {
      const childrenWidth = Array.from(sliderRef.current.children).map(
        (child: Element) => Math.round(child.getBoundingClientRect().width),
      );
      const maxScroll =
        sliderRef.current.scrollWidth - sliderRef.current.clientWidth;
      let leftCoords = childrenWidth.reduce(
        (acc, width) => [
          ...acc,
          Math.min(maxScroll, acc[acc.length - 1] + width),
        ],
        [0],
      );
      leftCoords.pop();
      leftCoords = Array.from(new Set(leftCoords));
      setChildrenLeftCoords(leftCoords);
      return [...leftCoords];
    }
    return [];
  }, []);

  const canGoLeft = useMemo(() => currentIndex > 0, [currentIndex]);
  const canGoRight = useMemo(
    () => currentIndex < childrenLeftCoords.length - 1,
    [currentIndex, childrenLeftCoords],
  );

  useEffect(() => {
    refreshChildrenLeftCoords();
  }, [refreshChildrenLeftCoords]);

  const onScroll = useCallback(() => {
    if (sliderRef.current) {
      let newSelectedTabIndex: number;
      if (
        sliderRef.current.scrollLeft >=
        sliderRef.current.scrollWidth - sliderRef.current.clientWidth
      ) {
        newSelectedTabIndex = childrenLeftCoords.length - 1;
      } else {
        newSelectedTabIndex = childrenLeftCoords.filter(
          // @ts-expect-error because TS is stupid sometimes
          (leftCoord) => leftCoord <= sliderRef.current.scrollLeft - 10,
        ).length;
      }
      if (currentIndex !== newSelectedTabIndex) {
        setCurrentIndex(newSelectedTabIndex);
      }

      if (!hasScrolledOnceRef.current) {
        hasScrolledOnceRef.current = true;
      }
    }
  }, [currentIndex, childrenLeftCoords]);

  const goToIndex = useCallback(
    (index: number) => {
      if (sliderRef.current) {
        const leftCoords = refreshChildrenLeftCoords();
        const targetIndex = Math.min(leftCoords.length - 1, index);

        // eslint-disable-next-line no-nested-ternary
        const scrollBehavior = !firstLoadRef.current
          ? 'smooth'
          : hasScrolledOnceRef.current
          ? 'smooth'
          : 'instant';

        sliderRef.current.scrollTo({
          left: leftCoords[targetIndex],
          behavior: scrollBehavior,
        });
      }
    },
    [refreshChildrenLeftCoords],
  );

  const goLeft = useCallback(() => {
    goToIndex(currentIndex - 1);
  }, [currentIndex, goToIndex]);

  const goRight = useCallback(() => {
    goToIndex(currentIndex + 1);
  }, [currentIndex, goToIndex]);

  useEffect(() => {
    if (onSlideChange) {
      onSlideChange(currentIndex);
    }
  }, [onSlideChange, currentIndex]);

  useEffect(() => {
    if (
      previousSelectedTabIndex !== selectedSlideIndex &&
      selectedSlideIndex !== currentIndex &&
      !isFullScreenImageModalOpen
    ) {
      goToIndex(selectedSlideIndex);
    }
  }, [previousSelectedTabIndex, selectedSlideIndex, currentIndex, goToIndex]);

  useEffect(() => {
    goToIndex(selectedSlideIndex);
  }, []);

  useEffect(() => {
    if (isFullScreenImageModalOpen) {
      goToIndex(selectedSlideIndex);
      setCurrentIndex(selectedSlideIndex); // explicitly update currentIndex
    }
  }, [isFullScreenImageModalOpen, selectedSlideIndex, goToIndex]);

  const formattedChildren = useMemo(
    () =>
      Children.map(children, (child) =>
        isValidElement(child) ? (
          <div
            key={child.key}
            className={`fit-${(child.props as { fit?: string })?.fit ?? fit}`}
          >
            {child}
          </div>
        ) : null,
      )?.filter((e) => !!e),
    [children],
  );

  useEffect(() => {
    if (firstLoadRef.current) {
      firstLoadRef.current = false;
    }
  }, []);

  return (
    <Wrapper isFullScreenImageModalOpen={isFullScreenImageModalOpen}>
      {showArrows && childrenLeftCoords.length > 1 && (
        <>
          <ArrowLeft
            isFullScreenImageModalOpen={isFullScreenImageModalOpen}
            aria-label={t('previousPhoto')}
            disabled={!canGoLeft}
            className="carousel-arrow carousel-arrow-left"
            Icon={IconLeft}
            onClick={goLeft}
          />
          <ArrowRight
            isFullScreenImageModalOpen={isFullScreenImageModalOpen}
            aria-label={t('nextPhoto')}
            disabled={!canGoRight}
            className="carousel-arrow carousel-arrow-right"
            Icon={IconRight}
            onClick={goRight}
          />
        </>
      )}
      <Slider
        role="region"
        aria-label={t('mediaSlides')}
        ref={sliderRef}
        onScroll={onScroll}
        className="carousel-slider"
      >
        {formattedChildren}
      </Slider>
    </Wrapper>
  );
};

export default MinimalCarousel;
